<template>
  <div class="variants-modal">
    <div
      class="variants-modal__default"
      @click="
        () => {
          toggle()
          $emit('track', 'Variant')
        }
      "
    >
      <FontAwesomeIcon :icon="['fal', 'copy']" size="3x" />
      <h3>{{ variantLabel }}</h3>
      <p>{{ $t('productVariantHelptext') }}</p>
      <ul v-if="variants.length" class="variants-modal__default__items">
        <li v-for="variant in variants" class="variants-modal__default__items--element">
          <Badge :value="variant.name" />
        </li>
        <li v-if="excessiveProductLabel">
          <Badge :value="excessiveProductLabel" />
        </li>
      </ul>
    </div>
    <Modal :show="show" class="variants-modal__main">
      <Card theme="flat">
        <template #default>
          <header>
            <h2>{{ $tc('variants', 0) }}</h2>
            <ButtonComponent :label="$t('close')" @click="mutate('reset')" />
          </header>
          <InputComponent
            v-model="data.category"
            data-field="category"
            :placeholder="$tc('variantCategoryPlaceholder')"
            :label="$tc('category', 1)"
            required
            validate
          />
          <section class="variants-modal__main__items">
            <h4>{{ $tc('variants', 0) }}</h4>
            <ButtonComponent
              class="variants-modal__main__items__item--create"
              :label="$t('create')"
              :icon="['fal', 'plus-circle']"
              icon-placement="left"
              @click="mutate('init')"
            />
            <section
              v-for="variant of variants"
              :class="[
                'variants-modal__main__items__item',
                { 'variants-modal__main__items__item--selected': variant.isDefault },
              ]"
            >
              <img v-if="variant.image" :src="variant.image.url" :alt="variant.name" />
              <ButtonComponent
                class="variants-modal__main__items__item__label"
                :label="variant.name"
                @click="mutate('edit', variant)"
              />
              <ButtonComponent
                data-type="action"
                class="variants-modal__main__items__item--edit"
                theme="primary"
                :icon="['far', 'edit']"
                @click="mutate('edit', variant)"
              />
              <ButtonComponent
                data-type="action"
                class="variants-modal__main__items__item--remove"
                theme="error"
                @click="mutate('remove', variant)"
                :icon="['fal', 'trash']"
              />
            </section>
            <Alert v-if="alerts.length" theme="warning">{{ alerts.join(', ') }}</Alert>
          </section>
        </template>
        <template #footer>
          <div class="row g-2 end flex-1">
            <ButtonComponent :label="$t('delete')" theme="error" @click="removeDialog = true" />
            <section>
              <ButtonComponent :label="$t('cancel')" @click="mutate('reset')" />
              <ButtonComponent
                theme="primary"
                :label="$t('confirm')"
                :icon="['fal', 'arrow-to-right']"
                :disabled="variantDisabled"
                @click="submit"
              />
            </section>
          </div>
        </template>
      </Card>
    </Modal>
    <Modal :show="variantDialog" class="variants-modal__variant">
      <Card theme="flat">
        <template #default>
          <header>
            <h2>{{ variantItemLabel }}</h2>
          </header>
          <section>
            <InputComponent
              v-model="variant.internalId"
              data-field="internalId"
              :label="$t('internalId')"
              :sublabel="$t('internalIdHelpText')"
              required
              :validate="validateInteralId"
              :validation-message="$t('internalIdValidationMessage')"
            />
            <InputComponent v-model="variant.name" data-field="name" :label="$t('name')" required validate />
            <InputComponent :label="$t('description')">
              <RichText v-model="variant.description" :commands="['bold', 'italic']" />
            </InputComponent>
            <MediaComponent
              v-model="variant.image"
              :label="$tc('image', 1)"
              :upload-options="{ compression: { maxWidth: 1920 } }"
              data-field="image"
              size="contain"
              required
              :enableDescription="false"
              :enableAlt="false"
              :multiple="false"
            />
            <CheckboxComponent
              v-model="variant.isDefault"
              :label="selectLabel.label"
              :sublabel="selectLabel.subLabel"
            />
          </section>
        </template>
        <template #footer>
          <div class="row g-2 end flex-1">
            <ButtonComponent theme="error" :label="$t('delete')" @click="mutate('remove')" />
            <section>
              <ButtonComponent :label="$t('cancel')" @click="mutate('resetVariant')" />
              <ButtonComponent
                theme="primary"
                :label="$t('confirm')"
                :icon="['fal', 'arrow-to-right']"
                :disabled="variantItemDisabled"
                @click="mutate('save')"
              />
            </section>
          </div>
        </template>
      </Card>
    </Modal>
    <RemoveComponent
      :show.sync="removeDialog"
      :title="`${$t('delete')} ${$tc('variants', 0).toLowerCase()}`"
      :promise="promise"
      :action="removeVariants"
      :resource="$tc('variants', 0)"
    />
  </div>
</template>

<script>
import Card from '@/components/Card'
import Portal from '@/components/Portal'
import { ModalComponent as Modal } from 'vue-elder-modal'
import { ButtonComponent } from 'vue-elder-button'
import { InputComponent } from 'vue-elder-input'
import RemoveComponent from '@/components/Remove'
import { MediaComponent } from '@kvass/media'
import RichText from '@kvass/rich-text'
import { CheckboxComponent } from 'vue-elder-checkbox'
import ObjectId from 'mongo-objectid'
import ProductApi from '@/api/Product'
import { AlertComponent as Alert } from 'vue-elder-alert'
import { Clone } from '@/utils'
import Badge from '@/components/Badge'

export default {
  props: {
    id: String,
    value: Object,
  },
  data() {
    return {
      variant: {},
      data: { category: null, items: [] },
      promise: null,
      show: false,
      removeDialog: false,
      variantDialog: false,
    }
  },
  watch: {
    show: {
      handler(val) {
        if (!val) return
        const cloned = Clone(this.value || {})
        if (cloned.items && cloned.items.length > 0) {
          cloned.items = cloned.items.map(item => {
            item.id = new ObjectId().hex
            return item
          })
        }
        this.data = { category: null, items: [], ...cloned }
      },
      immediate: true,
    },
  },
  computed: {
    variantLabel() {
      if (!this.variants || this.variants.length === 0)
        return `${this.$t('createResource', { resource: this.$tc('variants', 0).toLowerCase() })}`
      return `${this.$tc('variants', 0)} (${this.variants.length})`
    },
    variantItemLabel() {
      const isNew = this.variants.length === 0 || this.variants.every(item => this.variant.id !== item.id)
      return isNew ? this.$t('create') : this.$t('changeX', { x: this.$tc('variants', 1).toLowerCase() })
    },
    variantDisabled() {
      const isDisabled =
        !this.data.category ||
        this.variants.length === 0 ||
        (this.variants.length > 0 && !this.variants.some(item => item.isDefault))
      return !!isDisabled
    },
    variantDuplicateInternalId() {
      return this.variants.some(item => item.internalId === this.variant.internalId && item.id !== this.variant.id)
    },
    variantItemDisabled() {
      return ['name', 'image', 'internalId'].some(key => !this.variant[key]) || this.variantDuplicateInternalId
    },
    variants() {
      const items = this.data.items.length ? this.data.items : (this.value || {}).items || []
      return items
        .sort((a, b) => {
          if (a.isDefault) return -1
          if (b.isDefault) return 1
          return 0
        })
        .filter(item => !item.delete)
    },
    hasSelectedVariant() {
      return (this.variants || []).some(item => item.isDefault)
    },
    selectLabel() {
      const item = (this.variants || []).find(item => item.id === this.variant.id) || {}
      const label = this.$t('setDefaultVariant')
      const subLabel =
        this.hasSelectedVariant && (!item.isDefault || this.variantDuplicateInternalId)
          ? this.$t('variantAlreadySelected')
          : !this.hasSelectedVariant
          ? this.$t('defaultVariantSublabel')
          : ''

      return { label, subLabel }
    },
    alerts() {
      const alerts = []
      if (this.variants.length && this.variantDisabled) alerts.push(this.$t('variantDisabledHelpText'))
      return alerts
    },
    excessiveProductLabel() {
      if (!this.variants || this.variants.length < 4) return
      return `+ ${this.variants.length - 3}`
    },
  },
  methods: {
    validateInteralId(val) {
      return !this.variantDuplicateInternalId && val && val.length > 0
    },
    resetData() {
      this.data = { category: null, items: [] }
      this.variant = null
    },
    clear() {
      this.resetData()
      this.hideAll()
      this.$emit('input', null)
    },
    removeVariants() {
      if (!this.value || !this.id) return this.clear()
      this.promise = ProductApi.mutate({ id: this.id, variants: null }).then(_ => {
        this.promise = null
        this.clear()
      })
    },
    setCorrectSelection() {
      if (!this.variant.isDefault) return
      this.data.items = this.data.items.map(item => {
        if (item.id === this.variant.id) return this.variant
        item.isDefault = false
        return item
      })
    },
    saveVariant() {
      this.variantDialog = false
      this.setCorrectSelection()
      const index = this.data.items.findIndex(item => item.id === this.variant.id)
      if (index === -1) {
        this.data.items.push(this.variant)
        return
      }
      this.$set(this.data.items, index, this.variant)
      this.variant = {}
    },
    mutate(action, payload = {}) {
      switch (action) {
        case 'init':
          this.variant = {
            id: new ObjectId().hex,
            name: null,
            description: null,
            image: null,
            isDefault: false,
          }
          this.toggle('variant')
          break
        case 'remove': {
          const id = payload.id || this.variant.id
          this.data.items = this.data.items.map(item => {
            if (item.id === id) return { ...item, delete: true }
            return item
          })
          this.variantDialog = false
          break
        }
        case 'edit':
          this.variant = Clone(payload)
          this.toggle('variant')
          break
        case 'save': {
          this.saveVariant()
          break
        }
        case 'resetVariant':
          this.variantDialog = false
          break
        case 'reset': {
          this.variant = {}
          this.data = this.value ? Clone(this.value) : { category: null, items: [] }
          this.hideAll()
          break
        }
      }
    },
    hideAll() {
      this.show = false
      this.removeDialog = false
      this.variantDialog = false
    },
    toggle(action) {
      switch (action) {
        case 'remove':
          this.removeDialog = !this.removeDialog
          break
        case 'variant':
          this.variantDialog = !this.variantDialog
          break
        default:
          this.show = !this.show
          break
      }
    },
    submit() {
      this.$emit('input', {
        ...this.data,
        items: this.data.items
          .filter(item => !item.delete)
          .map(item => {
            delete item.id
            return item
          }),
      })
      this.resetData()
      this.hideAll()
    },
  },
  components: {
    Badge,
    Card,
    Portal,
    MediaComponent,
    Modal,
    ButtonComponent,
    InputComponent,
    RemoveComponent,
    RichText,
    CheckboxComponent,
    Alert,
  },
}
</script>

<style lang="scss">
[data-field='image'] {
  .kvass-media-droparea {
    min-height: 300px;
  }
}

.variants-modal {
  &__main,
  &__variant {
    .card {
      width: 100%;
      max-width: 800px;
      min-height: 600px;

      &__content {
        display: flex;
        flex-direction: column;
        gap: 1rem;

        header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid var(--light-grey);
          padding-bottom: 1rem;

          h2 {
            margin: 0;
          }
        }
      }
    }
  }

  &__variant,
  &__main {
    .card {
      &__footer {
        .end {
          display: flex;
          justify-content: space-between;
          width: 100%;

          section {
            display: flex;
            gap: 1rem;
          }
        }
      }
    }
  }

  &__main {
    .card {
      max-width: 950px;
      width: 100%;
    }

    &__items {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(30%, 1fr));
      gap: 1rem;

      h4 {
        margin-bottom: 0;
        margin-top: 1rem;
        grid-column: 1 / -1;
      }

      .alert {
        grid-column: 1 / -1;
      }

      &__item {
        display: grid;
        grid-template-columns: 50px 1fr 2rem 2rem;
        gap: 0.6rem;
        width: 100%;
        position: relative;
        align-items: center;
        border: 1px solid var(--grey);
        border-radius: var(--border-radius);
        padding-right: 0.2rem;

        &--selected {
          border-width: 1px;
          border-color: css-alpha('primary', 0.9);
          color: var(--primary);
          font-weight: 500;
        }

        &:hover {
          [data-type='action'] {
            opacity: 0.4;
            &:hover {
              opacity: 1;
            }
          }
        }

        &__label {
          border: none;
          background: none;
          justify-content: flex-start;
          min-height: 50px;

          &:hover {
            &:before {
              opacity: 0 !important;
            }
          }

          span {
            display: block;
            max-width: 14ch;
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
            flex-grow: 0;
            padding: 0;
          }
        }

        button {
          border: none;
          &:hover {
            cursor: pointer;
          }
        }

        &--edit,
        &--remove {
          opacity: 0;
          transition: opacity 150ms ease;
          width: 2rem;
          height: 2rem;
          border: none;
          border-radius: 100%;
          position: relative;
          z-index: 3;
        }

        &--create {
          background-color: css-alpha('primary', 0.1);
          border-color: css-alpha('primary', 0.25);
          border-style: dashed;
          justify-content: center;
          align-items: center;

          span {
            flex-grow: 0;
          }
        }

        img {
          width: 50px;
          height: 100%;
          object-fit: cover;
          border-top-left-radius: var(--border-radius);
          border-bottom-left-radius: var(--border-radius);
        }
      }
    }
  }

  &__variant {
    .card {
      &__content {
        &__header {
          margin-bottom: 1rem;
        }

        section {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
      }
    }
  }

  .elder-button {
    &--error {
      background-color: #9c23230d !important;
      border-color: #9c2323 !important;
      color: #9c2323 !important;

      &:hover {
        background-color: #9c2323 !important;
        color: white !important;
      }
    }
  }
}
</style>
